import React from 'react';
import { Box } from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

// Animated styles injected into the page (reused)
export const PowerFlowStyles = () => (
    <style>{`
        @keyframes flowDown {
            0% { stroke-dashoffset: 20; }
            100% { stroke-dashoffset: 0; }
        }
        @keyframes flowUp {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: 20; }
        }
    `}</style>
);

const TopologyDiagram = ({
    isDark = true,
    solarKw = 0,
    batteryKw = 0, // Positive = discharging (source), Negative = charging (load)
    loadKw = 0,
    gridKw = 0,
    evKw = 0
}) => {
    const assetBg = isDark ? '#242424' : '#f5f5f5';
    const hubBg = isDark ? '#1a1a1a' : '#e8e8e8';
    const gridPattern = isDark ? 'rgba(63, 106, 100, 0.08)' : 'rgba(63, 106, 100, 0.15)';
    const liveDataColor = '#3f6a64';

    // Battery State Logic
    const isBatteryDischarging = batteryKw > 0;
    const isBatteryCharging = batteryKw < 0;
    const absBatteryKw = Math.abs(batteryKw);

    // Solar State Logic
    const isSolarActive = solarKw > 5;

    return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <PowerFlowStyles />
            <svg viewBox="0 0 700 270" style={{ width: '100%', height: '100%', minHeight: 200 }} preserveAspectRatio="xMidYMid meet">
                <defs>
                    <pattern id="grid" width="25" height="25" patternUnits="userSpaceOnUse">
                        <path d="M 25 0 L 0 0 0 25" fill="none" stroke={gridPattern} strokeWidth="0.5" />
                    </pattern>
                </defs>
                <rect width="700" height="270" fill="url(#grid)" />

                {/* ROW 1: AC LAYER (Top) */}

                {/* 480VAC Bus - Spans Top */}
                <rect x="50" y="70" width="600" height="6" fill={vortxColors.teal} rx="2" opacity="0.9" />
                <text x="350" y="60" textAnchor="middle" fill={vortxColors.teal} fontFamily={vortxTypography.heading} fontSize="8" fontWeight="700">480VAC BUS</text>

                {/* Grid - Top Left, feeds down to AC Bus */}
                <g transform="translate(80, 20)">
                    <rect x="-25" y="-14" width="50" height="28" rx="3" fill={assetBg} stroke={vortxColors.teal} strokeWidth="1.5" />
                    <text x="0" y="-2" textAnchor="middle" fill={vortxColors.teal} fontFamily={vortxTypography.heading} fontSize="7" fontWeight="700">GRID</text>
                    <text x="0" y="8" textAnchor="middle" fill="#4CAF50" fontFamily="Inter" fontSize="6">
                        {Math.round(gridKw)} kW
                    </text>
                </g>
                <line x1="80" y1="34" x2="80" y2="70" stroke={vortxColors.teal} strokeWidth="1.5" />

                {/* Facility - Top Right, branches up from AC Bus */}
                <g transform="translate(620, 20)">
                    <rect x="-25" y="-14" width="50" height="28" rx="3" fill={assetBg} stroke={vortxColors.teal} strokeWidth="1.5" />
                    <text x="0" y="-2" textAnchor="middle" fill={vortxColors.teal} fontFamily={vortxTypography.heading} fontSize="7" fontWeight="700">FACILITY</text>
                    <text x="0" y="8" textAnchor="middle" fill={vortxColors.stone} fontFamily="Inter" fontSize="6">
                        {Math.round(loadKw)} kW
                    </text>
                </g>
                <line x1="620" y1="34" x2="620" y2="70" stroke={vortxColors.teal} strokeWidth="1.5" strokeDasharray="4 2" />


                {/* ROW 2: CONVERSION (Middle) */}

                {/* DC-AC Inverter - Center */}
                <g transform="translate(350, 110)">
                    <line x1="0" y1="-34" x2="0" y2="-12" stroke={vortxColors.teal} strokeWidth="2" />
                    <rect x="-25" y="-12" width="50" height="24" rx="2" fill={hubBg} stroke={liveDataColor} strokeWidth="1.5" />
                    <text x="0" y="4" textAnchor="middle" fill={vortxColors.gold} fontFamily={vortxTypography.heading} fontSize="7" fontWeight="800">EDDi-X</text>
                    <line x1="0" y1="12" x2="0" y2="40" stroke={vortxColors.gold} strokeWidth="2" />
                </g>


                {/* ROW 3: DC LAYER (Bottom) */}

                {/* 700VDC Bus - Spans Bottom */}
                <rect x="150" y="150" width="400" height="6" fill={vortxColors.gold} rx="2" />
                <text x="160" y="145" textAnchor="start" fill={vortxColors.gold} fontFamily={vortxTypography.heading} fontSize="8" fontWeight="700">700VDC BUS</text>

                {/* Solar - Bottom Left */}
                <g transform="translate(200, 200)">
                    <path
                        d="M 0 -44 L 0 -18"
                        stroke={vortxColors.gold}
                        strokeWidth="2.5"
                        strokeDasharray={isSolarActive ? "6 3" : "none"}
                        strokeOpacity={isSolarActive ? 1 : 0.3}
                        style={isSolarActive ? { animation: 'flowUp 0.6s linear infinite' } : {}}
                    />
                    <rect x="-30" y="-18" width="60" height="36" rx="3" fill={assetBg} stroke={vortxColors.gold} strokeWidth="1.5" />
                    <text x="0" y="-4" textAnchor="middle" fill={vortxColors.gold} fontFamily={vortxTypography.heading} fontSize="8" fontWeight="700">SOLAR</text>
                    <text x="0" y="8" textAnchor="middle" fill={isSolarActive ? liveDataColor : vortxColors.stone} fontFamily="Inter" fontSize="7">
                        {Math.round(solarKw)} kW
                    </text>
                </g>

                {/* BESS - Bottom Center */}
                <g transform="translate(350, 200)">
                    {/* BESS Connection Line - Discharging (Up) or Charging (Down) */}
                    <path
                        d="M 0 -44 L 0 -18"
                        stroke={vortxColors.gold}
                        strokeWidth="2.5"
                        strokeDasharray={(!isBatteryDischarging && !isBatteryCharging) ? "none" : "6 3"}
                        opacity={(!isBatteryDischarging && !isBatteryCharging) ? 0.3 : 1}
                        style={{
                            animation: isBatteryDischarging
                                ? 'flowUp 0.6s linear infinite' // Backward along Down-Path = Upward Flow
                                : (isBatteryCharging ? 'flowDown 0.6s linear infinite' : 'none') // Forward along Down-Path = Downward Flow
                        }}
                    />

                    <rect x="-30" y="-18" width="60" height="36" rx="3" fill={assetBg} stroke={vortxColors.gold} strokeWidth="1.5" />
                    <text x="0" y="-4" textAnchor="middle" fill={vortxColors.gold} fontFamily={vortxTypography.heading} fontSize="8" fontWeight="700">BESS</text>
                    <text x="0" y="8" textAnchor="middle" fill={absBatteryKw > 0 ? liveDataColor : vortxColors.stone} fontFamily="Inter" fontSize="7">
                        {Math.round(absBatteryKw)} kW
                    </text>
                </g>

                {/* EVSE - Bottom Right */}
                <g transform="translate(500, 200)">
                    <path
                        d="M 0 -44 L 0 -18"
                        stroke={vortxColors.gold}
                        strokeWidth="2.5"
                        strokeDasharray={evKw > 0 ? "6 3" : "none"}
                        strokeOpacity={evKw > 0 ? 1 : 0.3}
                        style={evKw > 0 ? { animation: 'flowDown 0.6s linear infinite' } : {}}
                    />
                    <rect x="-30" y="-18" width="60" height="36" rx="3" fill={assetBg} stroke={vortxColors.gold} strokeWidth="1.5" />
                    <text x="0" y="-4" textAnchor="middle" fill={vortxColors.gold} fontFamily={vortxTypography.heading} fontSize="8" fontWeight="700">EVSE</text>
                    <text x="0" y="8" textAnchor="middle" fill={liveDataColor} fontFamily="Inter" fontSize="7">
                        {Math.round(evKw)} kW
                    </text>
                </g>

            </svg>
        </Box>
    );
};

export default TopologyDiagram;
